import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PHONE_PREFIXES, STRIP_LEADING_ZERO_COUNTRIES, getFlagEmoji } from '../lib/countries';

const COUNTRY_CODES = Object.keys(PHONE_PREFIXES);

/**
 * Parse a full international number into { countryCode, national }.
 * Tries longest dialCode first to avoid ambiguity (e.g. +237 vs +23).
 */
function parseFullNumber(value) {
  if (!value || !value.startsWith('+')) return null;

  // Sort entries by dialCode length descending for greedy match
  const sorted = COUNTRY_CODES
    .map(code => ({ code, ...PHONE_PREFIXES[code] }))
    .sort((a, b) => b.dialCode.length - a.dialCode.length);

  for (const entry of sorted) {
    if (value.startsWith(entry.dialCode)) {
      return {
        countryCode: entry.code,
        national: value.slice(entry.dialCode.length),
      };
    }
  }
  return null;
}

const PhoneInput = ({
  value = '',
  onChange,
  defaultCountryCode = 'FR',
  label,
  required = false,
  error,
  id,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(defaultCountryCode);
  const [nationalNumber, setNationalNumber] = useState('');
  const [touched, setTouched] = useState(false);
  const [digitError, setDigitError] = useState('');
  const userChangedCountry = useRef(false);

  // Parse initial value on mount or when value changes externally
  useEffect(() => {
    if (!value) {
      setNationalNumber('');
      return;
    }
    const parsed = parseFullNumber(value);
    if (parsed) {
      setSelectedCountry(parsed.countryCode);
      setNationalNumber(parsed.national);
    } else {
      // Unknown format — put raw value in national field
      setNationalNumber(value);
    }
  }, []); // Only on mount — after that, internal state drives

  // When defaultCountryCode changes (e.g. user picks different destination),
  // update select only if user hasn't manually picked a country
  useEffect(() => {
    if (!userChangedCountry.current && defaultCountryCode) {
      setSelectedCountry(defaultCountryCode);
    }
  }, [defaultCountryCode]);

  const prefix = PHONE_PREFIXES[selectedCountry] || PHONE_PREFIXES.FR;

  const emitChange = useCallback((country, national) => {
    const p = PHONE_PREFIXES[country] || PHONE_PREFIXES.FR;
    const digits = national.replace(/\D/g, '');
    if (digits) {
      onChange(p.dialCode + digits);
    } else {
      onChange('');
    }
  }, [onChange]);

  const formatNational = (digits) => {
    if (!digits) return '';
    // Groups of 2 from right: "612345678" → "6 12 34 56 78"
    const parts = [];
    let i = digits.length;
    while (i > 0) {
      const start = Math.max(0, i - 2);
      parts.unshift(digits.slice(start, i));
      i = start;
    }
    return parts.join(' ');
  };

  const handleNationalChange = (e) => {
    let raw = e.target.value.replace(/[^\d]/g, '');

    // Auto-strip leading 0 for countries where it's a local convention
    if (raw.startsWith('0') && STRIP_LEADING_ZERO_COUNTRIES.includes(selectedCountry)) {
      raw = raw.slice(1);
    }

    // Enforce max digits for selected country
    const maxDigits = prefix.digits || 15;
    if (raw.length > maxDigits) {
      raw = raw.slice(0, maxDigits);
    }

    setNationalNumber(raw);
    setDigitError('');
    emitChange(selectedCountry, raw);
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;
    userChangedCountry.current = true;
    setSelectedCountry(code);
    setDigitError('');
    emitChange(code, nationalNumber);
  };

  const handleBlur = () => {
    setTouched(true);
    const digits = nationalNumber.replace(/\D/g, '');
    if (digits && digits.length !== prefix.digits) {
      setDigitError(`${prefix.digits} chiffres attendus`);
    } else {
      setDigitError('');
    }
  };

  const displayError = error || (touched ? digitError : '');
  const hasError = !!displayError;

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}{required && ' *'}</Label>}
      <div className="flex gap-1">
        {/* Country selector */}
        <select
          value={selectedCountry}
          onChange={handleCountryChange}
          className={`shrink-0 w-[110px] border rounded-md px-2 py-2 text-sm bg-background ${
            hasError ? 'border-red-500' : 'border-input'
          }`}
          aria-label="Indicatif pays"
        >
          {COUNTRY_CODES.map(code => (
            <option key={code} value={code}>
              {getFlagEmoji(code)} {PHONE_PREFIXES[code].dialCode}
            </option>
          ))}
        </select>

        {/* National number input */}
        <Input
          id={id}
          type="tel"
          value={formatNational(nationalNumber)}
          onChange={handleNationalChange}
          onBlur={handleBlur}
          placeholder={prefix.placeholder}
          required={required}
          className={`flex-1 font-mono tracking-wide ${hasError ? 'border-red-500' : ''}`}
        />
      </div>
      {displayError && <p className="text-xs text-red-500">{displayError}</p>}
    </div>
  );
};

export default PhoneInput;
